/**
 * useAdminPicksCharts Hook
 * Fetches chart data for the Picks Data Dashboard visualizations
 */

import type { ChartData, PicksFilters } from '@server/services/admin/adminPicksTypes.ts';
import { useCallback, useEffect, useState } from 'react';

export interface UseAdminPicksChartsParams {
  filters: PicksFilters;
}

export interface UseAdminPicksChartsResult {
  data: ChartData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch chart data for current filter set
 * Automatically refetches when filters change
 */
export function useAdminPicksCharts(params: UseAdminPicksChartsParams): UseAdminPicksChartsResult {
  const { filters } = params;

  const [data, setData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch chart data from API
   */
  const fetchChartData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = new URLSearchParams();

      if (Object.keys(filters).length > 0) {
        params.set('filters', JSON.stringify(filters));
      }

      const response = await fetch(`/api/admin/picks/charts?${params.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch chart data: ${response.statusText}`);
      }

      const result: ChartData = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch chart data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Refetch chart data
   */
  const refetch = useCallback(() => {
    fetchChartData();
  }, [fetchChartData]);

  /**
   * Fetch chart data when filters change
   */
  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  return {
    data,
    loading,
    error,
    refetch,
  };
}
